import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, BadgeCheck, ExternalLink, GitBranch, Loader2, MoreHorizontal, PackageCheck, RefreshCw, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import {
  CUSTOMER_IMPACT_LABELS, EFFORT_LABELS, FREQUENCY_LABELS, GITHUB_KIND_LABELS, GITHUB_STATE_LABELS, PRIORITY_LABELS, QUALIFICATION_FIELDS, RISK_LABELS,
  RELEASE_PRODUCT_LABELS, SEVERITY_LABELS, SOURCE_LABELS, formatDate, formatDateTime, historyText, otherMoves, pharmaciesText, primaryMove, productErrorMessage, releaseConfirmation,
} from "../lib/product";
import useRunningVersions from "../hooks/useRunningVersions";
import { ConfirmationBadge, PriorityBadge, StatusBadge, TypeBadge } from "../components/product/ProductBits";
import { CheckDialog } from "../components/product/ReleaseDialogs";
import {
  EditDialog, GithubDialog, MergeDialog, PlanningDialog, QualifyDialog, StatusDialog, TicketLinkDialog, UnmergeDialog,
} from "../components/product/ProductDialogs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuSeparator as DropdownMenuSeparator, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

const QUALIFICATION_WORDS = {
  customer_impact: CUSTOMER_IMPACT_LABELS, severity: SEVERITY_LABELS, frequency: FREQUENCY_LABELS, effort: EFFORT_LABELS, risk: RISK_LABELS, priority: PRIORITY_LABELS,
};

function SideRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm py-1.5">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-right text-slate-900 min-w-0 break-words">{children}</span>
    </div>
  );
}

function ItemLine({ item }) {
  return (
    <Link to={`/produit/${item.id}`} className="block text-sm hover:underline">
      <span className="text-xs text-slate-500 tabular-nums">{item.number}</span> {item.title}
    </Link>
  );
}

export default function ProductItemDetail() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const running = useRunningVersions();  // same reading as the version cards: a Jamora release is confirmed by what Jamora runs
  const [item, setItem] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [move, setMove] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [removing, setRemoving] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/product/items/${itemId}`);
      setItem(data);
      setPhase("ready");
    } catch (e) {
      setErrorText(e?.response?.status === 404 ? "Ce retour n'existe pas." : productErrorMessage(e, "Impossible de charger le retour."));
      setPhase("error");
    }
  }, [itemId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  const applied = (data, message) => {
    if (!data) { load(); return; } // a refused write: re-read the item as it is now
    setItem(data);
    setMove(null);
    setDialog(null);
    if (message) toast.success(message);
  };

  const unlink = async (kind, ref, message) => {
    setRemoving(ref);
    try {
      const url = kind === "ticket" ? `/product/items/${item.id}/tickets/${ref}` : `/product/items/${item.id}/github`;
      const { data } = await api.delete(url, kind === "github" ? { params: { url: ref } } : undefined);
      applied(data, message);
    } catch (e) {
      toast.error(productErrorMessage(e, "Impossible de retirer ce lien."));
      load();
    } finally {
      setRemoving("");
    }
  };

  if (phase === "loading") return <div className="space-y-3" data-testid="product-item-loading"><Skeleton className="h-28 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="product-item-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{errorText}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to="/produit">Retour aux retours</Link></Button>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      </Card>
    );
  }

  const caps = item.capabilities || {};
  const primary = primaryMove(item);
  const moves = otherMoves(item);
  const merged = !!item.duplicate_of;
  const pharmacies = pharmaciesText(item);
  const canDelete = item.status === "proposed" && hasPermission(user, "product:delete");
  const hasMenu = caps.triage || caps.roadmap || moves.length > 0 || canDelete;

  return (
    <div className="space-y-4" data-testid="product-item-detail">
      <Link to="/produit" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Retours et feuille de route</Link>

      <Card className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="text-xs text-slate-500 tabular-nums" data-testid="product-item-number">{item.number}</div>
            <h1 className="font-display font-bold text-xl text-slate-900 break-words" data-testid="product-item-title">{item.title}</h1>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <StatusBadge status={item.status} /><TypeBadge type={item.type} /><PriorityBadge priority={item.priority} />
              {pharmacies && <span className="text-xs text-slate-500 self-center" data-testid="product-item-pharmacies">{pharmacies}</span>}
            </div>
          </div>
          {hasMenu && !merged && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Autres actions" data-testid="product-item-more"><MoreHorizontal size={18} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" title="Actions sur le retour">
                {moves.map((m) => <DropdownMenuItem key={m.status} onClick={() => setMove(m)} data-testid={`product-move-${m.status}`}>{m.label}</DropdownMenuItem>)}
                {moves.length > 0 && <DropdownMenuSeparator />}
                {caps.triage && caps.editable && <DropdownMenuItem onClick={() => setDialog("edit")} data-testid="product-action-edit">Modifier</DropdownMenuItem>}
                {caps.triage && caps.editable && <DropdownMenuItem onClick={() => setDialog("qualify")} data-testid="product-action-qualify">Qualifier</DropdownMenuItem>}
                {caps.roadmap && caps.editable && <DropdownMenuItem onClick={() => setDialog("planning")} data-testid="product-action-planning">Planification</DropdownMenuItem>}
                {caps.triage && <DropdownMenuItem onClick={() => setDialog("ticket")} data-testid="product-action-ticket">Rattacher un ticket</DropdownMenuItem>}
                {caps.triage && <DropdownMenuItem onClick={() => setDialog("github")} data-testid="product-action-github">Ajouter un lien GitHub</DropdownMenuItem>}
                {canDelete && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="product-action-delete">Supprimer le retour…</DropdownMenuItem>}
                {caps.triage && caps.editable && item.children.length === 0 && <DropdownMenuItem onClick={() => setDialog("merge")} data-testid="product-action-merge">Fusionner dans un autre retour</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {merged && caps.triage && (
            <Button variant="outline" size="sm" onClick={() => setDialog("unmerge")} data-testid="product-action-unmerge">Défaire la fusion</Button>
          )}
        </div>
        {primary && !merged && (
          <div><Button onClick={() => setMove(primary)} className="w-full sm:w-auto" data-testid="product-primary">{primary.label}</Button></div>
        )}
      </Card>

      {merged && item.master && (
        <Card className="p-4 border-sky-200 bg-sky-50 text-sm text-sky-900" data-testid="product-merged-banner">
          Ce retour est fusionné dans <Link to={`/produit/${item.master.id}`} className="font-medium underline">{item.master.number} · {item.master.title}</Link>.
          Il ne s'avance plus : suivez l'élément maître.
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4 min-w-0">
          {(item.delivered_in.length > 0 || item.status === "delivered") && (
            <Card className="p-5 space-y-2" data-testid="product-item-delivery">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><PackageCheck size={14} className="text-slate-400" />Livraison</h2>
              {item.delivered_in.length === 0 ? <p className="text-sm text-slate-500">Aucune version rattachée.</p> : (
                <ul className="space-y-2">
                  {item.delivered_in.map((r) => (
                    <li key={r.id} className="space-y-1" data-testid={`product-item-release-${r.number}`}>
                      <Link to={`/produit/versions/${r.id}`} className="text-sm hover:underline">Version {r.label}
                        <span className="text-xs text-slate-500"> · {RELEASE_PRODUCT_LABELS[r.product]}{r.deployed_at ? ` · ${formatDate(r.deployed_at)}` : ""}</span></Link>
                      <div><ConfirmationBadge confirmation={releaseConfirmation(r, running.confirmedIds)} testid="product-item-confirmation" /></div>
                    </li>
                  ))}
                </ul>
              )}
              {item.status === "delivered" && (
                <div className="pt-2 border-t border-slate-100 space-y-1.5" data-testid="product-item-check">
                  {item.functional_check ? (
                    <p className="text-sm text-emerald-800 flex items-start gap-1.5"><BadgeCheck size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                      <span>Vérifié par {item.functional_check.by_name || "un compte"} le {formatDate(item.functional_check.at)}
                        {item.functional_check.note && <span className="block text-slate-600">« {item.functional_check.note} »</span>}</span></p>
                  ) : <p className="text-sm text-slate-600">À vérifier en conditions réelles. Cela ne bloque rien.</p>}
                  {caps.verify && <Button variant="outline" size="sm" onClick={() => setDialog("check")} data-testid="product-check">{item.functional_check ? "Vérifier à nouveau" : "Marquer comme vérifié"}</Button>}
                </div>
              )}
            </Card>
          )}

          {(item.description || item.decision_reason) && (
            <Card className="p-5 space-y-3">
              {item.description && (
                <div><h2 className="text-sm font-semibold text-slate-900">Description</h2>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line break-words" data-testid="product-item-description">{item.description}</p></div>
              )}
              {item.decision_reason && ["retained", "abandoned"].includes(item.status) && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="product-item-decision">
                  <h2 className="text-sm font-semibold text-slate-900">Décision</h2>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{item.decision_reason}</p>
                </div>
              )}
            </Card>
          )}

          <Card className="p-5 space-y-3" data-testid="product-item-qualification">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Qualification</h2>
              {caps.triage && caps.editable && <Button variant="ghost" size="sm" onClick={() => setDialog("qualify")}>Qualifier</Button>}
            </div>
            {QUALIFICATION_FIELDS.every((f) => !item[f.id]) ? (
              <p className="text-sm text-slate-500">Pas encore qualifié.</p>
            ) : (
              <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
                {QUALIFICATION_FIELDS.map((f) => (
                  <div key={f.id} className="flex justify-between gap-3"><dt className="text-slate-500">{f.label}</dt>
                    <dd className="text-slate-900" data-testid={`product-q-value-${f.id}`}>{item[f.id] ? QUALIFICATION_WORDS[f.id][item[f.id]] : <span className="text-slate-400">—</span>}</dd></div>
                ))}
              </dl>
            )}
          </Card>

          <Card className="p-5 space-y-3">
            <h2 className="text-sm font-semibold text-slate-900">Historique</h2>
            <ol className="space-y-3" data-testid="product-item-history">
              {item.history.map((h) => (
                <li key={h.id} className="flex gap-3 text-sm">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" aria-hidden="true" />
                  <div className="min-w-0">
                    <span className="text-slate-800">{historyText(h)}</span>
                    <span className="text-xs text-slate-500"> · {h.actor_name || "un compte"} · {formatDateTime(h.created_at)}</span>
                    {h.reason && <p className="text-slate-600 mt-0.5 whitespace-pre-line">« {h.reason} »</p>}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>

        <aside className="space-y-4 min-w-0">
          <Card className="p-5 divide-y divide-slate-100">
            <SideRow label="Source">{SOURCE_LABELS[item.source]}</SideRow>
            <SideRow label="Responsable">{item.owner_name || <span className="text-slate-400">Personne</span>}</SideRow>
            <SideRow label="Thème">{item.theme || <span className="text-slate-400">—</span>}</SideRow>
            <SideRow label="Version cible">{item.target_version || <span className="text-slate-400">—</span>}</SideRow>
            <SideRow label="Pharmacies touchées">
              {item.impacted_pharmacies || <span className="text-slate-400">—</span>}
              {item.pharmacies_estimate > 0 && <span className="block text-xs text-slate-500">dont {item.pharmacies_estimate} estimée{item.pharmacies_estimate > 1 ? "s" : ""}</span>}
            </SideRow>
            <SideRow label="Créé">{item.created_by_name} · {formatDate(item.created_at)}</SideRow>
            {item.reopens_item_id && <SideRow label="Reprend"><Link to={`/produit/${item.reopens_item_id}`} className="text-jamora hover:underline">l'élément livré</Link></SideRow>}
          </Card>

          <Card className="p-5 space-y-2" data-testid="product-item-tickets">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Tickets rattachés</h2>
              {caps.triage && !merged && <Button variant="ghost" size="sm" onClick={() => setDialog("ticket")}>Rattacher</Button>}
            </div>
            {item.tickets.length === 0 ? <p className="text-sm text-slate-500">Aucun ticket.</p> : (
              <ul className="space-y-1.5">
                {item.tickets.map((t) => (
                  <li key={t.id} className="flex items-start justify-between gap-2">
                    {t.visible ? (
                      <Link to={`/support/tickets/${t.id}`} className="text-sm hover:underline min-w-0 break-words"><span className="text-xs text-slate-500 tabular-nums">{t.number}</span> {t.title}</Link>
                    ) : <span className="text-sm text-slate-500 italic">Ticket que vous ne pouvez pas consulter</span>}
                    {caps.triage && (
                      <button type="button" aria-label="Détacher ce ticket" disabled={removing === t.id} onClick={() => unlink("ticket", t.id, "Ticket détaché")}
                        className="text-slate-400 hover:text-slate-700 shrink-0">{removing === t.id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5 space-y-2" data-testid="product-item-github">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><GitBranch size={14} className="text-slate-400" />GitHub</h2>
              {caps.triage && <Button variant="ghost" size="sm" onClick={() => setDialog("github")}>Ajouter</Button>}
            </div>
            {item.github.length === 0 ? <p className="text-sm text-slate-500">Aucun lien.</p> : (
              <ul className="space-y-1.5">
                {item.github.map((g) => (
                  <li key={g.url} className="flex items-start justify-between gap-2">
                    <a href={g.url} target="_blank" rel="noopener noreferrer" className="text-sm text-jamora hover:underline min-w-0 break-words inline-flex items-start gap-1">
                      <ExternalLink size={12} className="mt-1 shrink-0" aria-hidden="true" />
                      <span>{GITHUB_KIND_LABELS[g.kind]} {g.repo}{g.ref ? ` ${g.kind === "issue" || g.kind === "pull" ? "#" : ""}${g.ref}` : ""}
                        {g.state && <span className="text-xs text-slate-500"> · {GITHUB_STATE_LABELS[g.state]}</span>}</span>
                    </a>
                    {caps.triage && (
                      <button type="button" aria-label="Retirer ce lien" disabled={removing === g.url} onClick={() => unlink("github", g.url, "Lien retiré")}
                        className="text-slate-400 hover:text-slate-700 shrink-0">{removing === g.url ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-slate-500">Une issue fermée sur GitHub ne rend pas ce retour « livré » : seule une version livrée le fait.</p>
          </Card>

          {item.children.length > 0 && (
            <Card className="p-5 space-y-2" data-testid="product-item-children">
              <h2 className="text-sm font-semibold text-slate-900">Retours regroupés ({item.children.length})</h2>
              <ul className="space-y-1.5">{item.children.map((c) => <li key={c.id}><ItemLine item={c} /></li>)}</ul>
            </Card>
          )}

          {item.dependencies.length > 0 && (
            <Card className="p-5 space-y-2" data-testid="product-item-dependencies">
              <h2 className="text-sm font-semibold text-slate-900">Dépend de</h2>
              <ul className="space-y-1.5">{item.dependencies.map((d) => <li key={d.id} className="flex items-start justify-between gap-2"><ItemLine item={d} /><StatusBadge status={d.status} /></li>)}</ul>
            </Card>
          )}
        </aside>
      </div>

      {move && <StatusDialog item={item} move={move} onOpenChange={(open) => { if (!open) setMove(null); }} onDone={(data) => applied(data, "État mis à jour")} />}
      <EditDialog item={item} open={dialog === "edit"} onOpenChange={(open) => setDialog(open ? "edit" : null)} onDone={(data) => applied(data, "Retour modifié")} />
      <QualifyDialog item={item} open={dialog === "qualify"} onOpenChange={(open) => setDialog(open ? "qualify" : null)} onDone={(data) => applied(data, "Qualification enregistrée")} />
      <PlanningDialog item={item} open={dialog === "planning"} onOpenChange={(open) => setDialog(open ? "planning" : null)} onDone={(data) => applied(data, "Planification enregistrée")} />
      <MergeDialog item={item} open={dialog === "merge"} onOpenChange={(open) => setDialog(open ? "merge" : null)} onDone={(data) => applied(data, "Retour fusionné")} />
      <UnmergeDialog item={item} open={dialog === "unmerge"} onOpenChange={(open) => setDialog(open ? "unmerge" : null)} onDone={(data) => applied(data, "Fusion défaite")} />
      <TicketLinkDialog item={item} open={dialog === "ticket"} onOpenChange={(open) => setDialog(open ? "ticket" : null)} onDone={(data) => applied(data, "Ticket rattaché")} />
      <CheckDialog item={item} open={dialog === "check"} onOpenChange={(open) => setDialog(open ? "check" : null)} onDone={(data) => applied(data, "Vérification enregistrée")} />
      <GithubDialog item={item} open={dialog === "github"} onOpenChange={(open) => setDialog(open ? "github" : null)} onDone={(data) => applied(data, "Lien ajouté")} />
      <DeleteWithReasonDialog
        target={dialog === "delete" ? item : null} testId="delete-product" onClose={() => setDialog(null)}
        title={`Supprimer le retour ${item.number} ?`}
        description="Il va dans la corbeille et se restaure pendant 30 jours. Un retour déjà traité, lié à un ticket ou à une version s'abandonne ou se fusionne à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/product/items/${item.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/produit");
          } catch (e) {
            toast.error(productErrorMessage(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
    </div>
  );
}
